"use client";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import ProductAttributesBuilder from "./ProductAttributesBuilder";
import {
  attrsInitValues,
  getIndexOfChild,
  getUnavailableAttributes,
} from "@/app/components/common-addons-builder/addon-functions";
import Separator from "@/app/components/lib/Separator";
import AttributesStepsSelector from "./AttributesStepsSelector";
import { useGetCompanyQuery } from "@/api/AppApi/services/company";
import { AttributesSelectionMethods } from "@/api/AppApi/services/company/interfaces/ICompany";
import {
  fillSelectedAttributes,
  findUnavailableAttributes,
  findOutOfStockAttributes,
  findDisabledAttributes,
  mergeAttributeKeys,
} from "./helpers/functions";
import type { IAttiribs, IProductMinimizedData } from "./helpers/interfaces";
import { useGetCartQuery } from "@/api/AppApi/services/cart";


const findNonSelectableAttributes = (
  selectedAttributes: IAttiribs,
  product: IProductMinimizedData,
): IAttiribs =>
  mergeAttributeKeys(
    findUnavailableAttributes(selectedAttributes, product) as IAttiribs,
    findDisabledAttributes(selectedAttributes, product),
  );

interface Props {
  product: IProductMinimizedData;
  setSelectedChildIndex: (index: number | null) => void;
  initialValues?: number[];
  isTopWinner?: boolean;
  autoSelectFirstChild: boolean;
  detailsCountaineMaxWidth?: number;
  isProductBox?: boolean;
  setSelectedAttributesValues?: (selectedAttributes: number[]) => void;
  noStepper?: boolean;
  showAllAttributes?: boolean;
  allow_user_request_stock_availability_reminder?: boolean;
}
const Attributes: React.FC<Props> = ({
  product,
  setSelectedChildIndex,
  initialValues,
  isTopWinner,
  autoSelectFirstChild,
  detailsCountaineMaxWidth,
  isProductBox,
  setSelectedAttributesValues,
  noStepper,
  showAllAttributes,
  allow_user_request_stock_availability_reminder,
}) => {
  const getCart = useGetCartQuery();
  const cartItems = getCart?.data?.result?.items;
  const [selectedAttributes, setSelectedAttributes] = useState<IAttiribs>({});
  const hasAttributes = !!product?.attributes_builder?.length;
  // Both maps are pure functions of the current selection, the product and the
  // cart, so they are derived — that keeps the sold-out state in sync when the
  // cart (and therefore the sellable quantity) changes, not only on re-selection.
  const unavailabeAttributes = useMemo<IAttiribs>(
    () =>
      hasAttributes
        ? (findUnavailableAttributes(selectedAttributes, product) as IAttiribs)
        : {},
    [hasAttributes, selectedAttributes, product],
  );
  // Rendered like out-of-stock but never selectable — kept apart from the
  // unavailable map because unavailable options are hidden entirely.
  const disabledAttributes = useMemo<IAttiribs>(
    () =>
      hasAttributes ? findDisabledAttributes(selectedAttributes, product) : {},
    [hasAttributes, selectedAttributes, product],
  );
  const outOfStockAttributes = useMemo<IAttiribs>(
    () =>
      hasAttributes
        ? (findOutOfStockAttributes(
            selectedAttributes,
            product,
            cartItems,
          ) as IAttiribs)
        : {},
    [hasAttributes, selectedAttributes, product, cartItems],
  );
  const [currentAttributeToSelectIdx, setCurrentAttributeToSelectIdx] =
    useState(
      () =>
        fillSelectedAttributes(
          product.attributes_builder,
          0,
          mergeAttributeKeys(unavailabeAttributes, disabledAttributes),
        ).currentIndex,
    );
  const getCompany = useGetCompanyQuery();
  const company = getCompany?.data?.result;
  const { attributes_selection_methods } =
    company?.theme?.components?.product ?? {};
  const isSteper =
    attributes_selection_methods === AttributesSelectionMethods.cascading; // TODO need edit after backend finish
  useEffect(() => {
    if (
      !product ||
      product?.children?.length === 0 ||
      product?.attributes_builder?.length === 0
    )
      return;
    const selected: any = attrsInitValues(
      product.attributes_builder,
      initialValues,
    );
    let unAvailable = attrsInitValues(product.attributes_builder, []);
    if (initialValues) {
      for (const key of Object.keys(selected)) {
        getUnavailableAttributes(key, selected[key], product);
        unAvailable = {
          ...unAvailable,
          ...getUnavailableAttributes(key, selected[key], product),
        };
      }
    }
    const attrsWithSelectFirstChild = product?.attributes_builder?.filter(
      (attr) =>
        autoSelectFirstChild ||
        (attr?.data?.length === 1 &&
          !(unAvailable as IAttiribs)[attr.configurations.key].includes(
            attr.data?.[0]?.key,
          )),
    );
    const selectdAttersWithFirstOption: IAttiribs = {
      ...attrsWithSelectFirstChild.reduce((acc: IAttiribs, currentValue) => {
        const unavailable =
          findNonSelectableAttributes(acc, product)?.[
            currentValue.configurations.key
          ] || [];
        const outOfStock =
          (findOutOfStockAttributes(acc, product, cartItems) as IAttiribs)?.[
            currentValue.configurations.key
          ] || [];

        const foundOption =
          currentValue.data?.find(
            (at) =>
              !unavailable.includes(at.key) && !outOfStock.includes(at.key),
          ) ||
          currentValue.data?.find((at) => !unavailable.includes(at.key));

        acc[currentValue?.configurations?.key] = [foundOption?.key!];
        return acc;
      }, {}),
    };
    for (const property in selectdAttersWithFirstOption) {
      if (selectdAttersWithFirstOption[property]?.[0] === undefined) {
        selectdAttersWithFirstOption[property] = [];
      }
    }
    const selectedAttrs = { ...selected, ...selectdAttersWithFirstOption };
    const index = getIndexOfChild(product?.children, selectedAttrs); //null,-1 or index of child

    setSelectedAttributes(selectedAttrs);
    setSelectedChildIndex(index);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  const handleAttributesChanged = (
    key: string,
    attrib: string[],
    clearAhead = false,
    isautoSelectFirstChild = false,
  ) => {
    const newState = { ...selectedAttributes, [key]: attrib };
    const currentAttr = product.attributes_builder.findIndex(
      (attrib) => attrib.configurations.key === key,
    );
    if (clearAhead) {
      product.attributes_builder
        .filter((_, idx) => idx > currentAttr)
        .forEach((item) => (newState[item.configurations.key] = []));
    }
    if (isautoSelectFirstChild) {
      product.attributes_builder
        .filter((_, idx) => idx > currentAttr)
        .forEach((item) => {
          const unavailable =
            findNonSelectableAttributes(newState, product)?.[
              item.configurations.key
            ] || [];
          const outOfStock =
            (findOutOfStockAttributes(newState, product, cartItems) as IAttiribs)?.[
              item.configurations.key
            ] || [];

          const foundOption =
            item?.data?.find(
              (at) =>
                !unavailable.includes(at.key) && !outOfStock.includes(at.key),
            ) || item?.data?.find((at) => !unavailable.includes(at.key));

          newState[item.configurations.key] = [foundOption?.key!];

          for (const property in newState) {
            if (
              newState[property]?.[0] === undefined ||
              newState[property]?.find((value) => value === undefined)
            ) {
              newState[property] = [];
            }
          }
        });
    }
    setSelectedAttributes(newState);
    const index = getIndexOfChild(product?.children, newState); //null,-1 or index of child
    setSelectedChildIndex(index);
  };

  useEffect(() => {
    if (setSelectedAttributesValues)
      setSelectedAttributesValues?.(
        Object.values(selectedAttributes)
          .flat()
          .map((item) => Number(item)),
      );
  }, [selectedAttributes, setSelectedAttributesValues]);
  if (isSteper && !autoSelectFirstChild && !isProductBox && !noStepper)
    return (
      <AttributesStepsSelector
        {...product}
        attributesMaxWidth={detailsCountaineMaxWidth}
        currentAttributeToSelectIdx={currentAttributeToSelectIdx}
        setCurrentAttributeToSelectIdx={setCurrentAttributeToSelectIdx}
        selectedAttributes={selectedAttributes}
        handleAttributesChanged={handleAttributesChanged}
        unAvailableKeys={unavailabeAttributes}
        outOfStockKeys={outOfStockAttributes}
        disabledKeys={disabledAttributes}
        allow_user_request_stock_availability_reminder={
          allow_user_request_stock_availability_reminder
        }
        show_configurable_attribute_for_all_product={
          company?.theme?.components?.product
            ?.show_configurable_attribute_for_all_product
        }
      />
    );
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: isProductBox ? 8 : 24,
      }}
    >
      {product?.attributes_builder
        .filter((attr) => {
          if (!attr?.data?.length) return false;
          if (attr?.data?.length > 1) return true;
          return !!company?.theme?.components?.product
            ?.show_configurable_attribute_for_all_product;
        })
        .filter((_attr, idx) =>
          isProductBox && !showAllAttributes ? idx < 1 : true,
        )
        .map((attribute) => {
          const isForcedVisibleOneOption =
            attribute?.data?.length === 1 &&
            company?.theme?.components?.product
              ?.show_configurable_attribute_for_all_product;

          return (
            <div
              key={attribute.configurations.key}
              style={
                isForcedVisibleOneOption ? { pointerEvents: "none" } : undefined
              }
            >
              {!isTopWinner &&
                !isProductBox &&
                (attribute?.data?.length! > 1 || isForcedVisibleOneOption) && (
                  <Separator
                    startLabel={attribute.configurations.title}
                    marginBottom={8}
                  />
                )}
              {isProductBox && (
                <div className="start-from">
                  {attribute.configurations.title}
                </div>
              )}
              <ProductAttributesBuilder
                attributesMaxWidth={detailsCountaineMaxWidth}
                productChildren={product?.children!}
                attribute={attribute}
                unAvailabeKeys={
                  unavailabeAttributes[attribute.configurations.key]
                }
                outOfStockKeys={
                  outOfStockAttributes[attribute.configurations.key]
                }
                disabledKeys={disabledAttributes[attribute.configurations.key]}
                state={
                  !product.is_configurable &&
                  isForcedVisibleOneOption &&
                  attribute?.data?.[0]?.key
                    ? [attribute.data[0].key.toString()]
                    : selectedAttributes[attribute.configurations.key]
                }
                setState={(attrib) =>
                  handleAttributesChanged(
                    attribute.configurations.key,
                    attrib,
                    autoSelectFirstChild,
                    autoSelectFirstChild,
                  )
                }
                isProductBox={isProductBox}
                allow_user_request_stock_availability_reminder={
                  allow_user_request_stock_availability_reminder
                }
                isForcedVisibleOneOption={isForcedVisibleOneOption}
              />
            </div>
          );
        })}
    </div>
  );
};

export default Attributes;
